import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ChevronLeft } from 'lucide-react';
import { AgentChat, Model } from '../agentloop-ui';

export const ExplorationChat: React.FC = () => {
  const { id: explorationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [isInterrupting, setIsInterrupting] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('claude-haiku-4-5');

  // Setup stream URL
  useEffect(() => {
    if (!explorationId) return;

    const backendUrl =
      process.env.NODE_ENV === 'development'
        ? `http://localhost:8080/api/explorations/${explorationId}/listen`
        : `/api/explorations/${explorationId}/listen`;

    setStreamUrl(backendUrl);
  }, [explorationId]);

  // Setup auth token with periodic refresh
  useEffect(() => {
    const updateToken = async () => {
      const token = await getToken();
      setAuthToken(token);
    };

    // Initial token fetch
    updateToken();

    // Refresh token every 30 seconds to prevent expiration
    const intervalId = setInterval(updateToken, 30000);

    return () => clearInterval(intervalId);
  }, [getToken]);

  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const token = await getToken();
        const response = await fetch('/api/models', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // API returns array directly, not wrapped in object
          setModels(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };

    fetchModels();
  }, [getToken]);

  // Memoize auth headers to prevent unnecessary reconnections
  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
  }, [authToken]);

  // Send message handler
  const handleSendMessage = async (message: string) => {
    if (!explorationId || !message.trim()) return;

    try {
      const token = await getToken();
      const response = await fetch(
        `/api/explorations/${explorationId}/message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            model: selectedModel, // Include selected model
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Interrupt agent handler
  const handleInterrupt = async () => {
    if (!explorationId || isInterrupting) return;

    setIsInterrupting(true);
    try {
      const token = await getToken();
      await fetch(`/api/explorations/${explorationId}/interrupt`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Failed to interrupt:', error);
    } finally {
      setTimeout(() => {
        setIsInterrupting(false);
      }, 500);
    }
  };

  // Try again handler (resend last user message)
  const handleTryAgain = async () => {
    // For now, just let the user send a new message
    console.log('Try again clicked');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Exploration Mode
            </h1>
            <p className="text-sm text-gray-500">
              Let's figure out what you want to build
            </p>
          </div>
        </div>
      </div>

      {/* Agent Chat */}
      <div className="flex-1 overflow-hidden bg-white">
        {streamUrl && authToken && (
          <AgentChat
            streamUrl={streamUrl}
            streamMethod="POST"
            streamHeaders={authHeaders}
            autoConnect={true}
            onSendMessage={handleSendMessage}
            onInterrupt={handleInterrupt}
            onTryAgain={handleTryAgain}
            placeholder="Tell me about your idea..."
            workingText="Thinking..."
            className="h-full"
            showModelSelector={true}
            models={models}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
          />
        )}
      </div>
    </div>
  );
};
